/**
 * Single source of truth for the clinic's NAP+ (Name, Address, Phone, Hours, Geo, Social).
 *
 * IMPORTANT for Local SEO / Google Business Profile:
 * Every visible mention of name, address, phone, hours, geo coordinates, or
 * official social URLs on the site MUST derive from this object. Update once
 * here and every page (header, footer, schema, legal pages, edge functions,
 * NAP audit) stays consistent.
 */
export interface ClinicHoursRow {
  days: string;
  time: string;
  schema: string[];
  opens: string | null;
  closes: string | null;
}

export interface ClinicNAP {
  name: string;
  legalName: string;
  alternateName: string;
  shortName: string;
  tagline: string;
  founded: number;
  address: {
    street: string;
    locality: string;
    region: string;
    postalCode: string;
    country: string;
    countryCode: string;
  };
  geo: { latitude: number; longitude: number };
  phone: { international: string; display: string };
  whatsapp: { number: string; display: string; url: string };
  hours: ClinicHoursRow[];
  bookingNote: string;
  social: {
    website: string;
    websiteDisplay: string;
    instagram: string;
    instagramHandle: string;
  };
  bookingUrl: string;
}

export const clinicNAP: ClinicNAP = {
  name: "Dr Vahe Clinic",
  legalName: "Dr Vahe Clinic",
  alternateName: "Dr Vahe Blepharoplasty London",
  shortName: "Dr Vahe",
  tagline:
    "Excellence in medicine and aesthetics. Personalised treatments that empower you to look and feel your best.",
  founded: 2024,
  address: {
    street: "25 Ives Street",
    locality: "Chelsea",
    region: "London",
    postalCode: "SW3 2ND",
    country: "United Kingdom",
    countryCode: "GB",
  },
  geo: {
    latitude: 51.4925,
    longitude: -0.1689,
  },
  phone: {
    international: "+442033703503",
    display: "020 3370 3503",
  },
  whatsapp: {
    number: "+447537103040",
    display: "+44 7537 103040",
    url: "https://wa.me/447537103040",
  },
  hours: [
    { days: "Monday – Friday", time: "09:00 – 18:00", schema: ["Mo", "Tu", "We", "Th", "Fr"], opens: "09:00", closes: "18:00" },
    { days: "Saturday", time: "By appointment", schema: ["Sa"], opens: null, closes: null },
    { days: "Sunday", time: "Closed", schema: ["Su"], opens: null, closes: null },
  ],
  bookingNote: "By appointment only",
  social: {
    website: "https://www.doctorvahe.com",
    websiteDisplay: "www.doctorvahe.com",
    instagram: "https://instagram.com/dr.vaheclinic",
    instagramHandle: "@dr.vaheclinic",
  },
  bookingUrl: "https://www.blepharoplasty.drvaheclinic.com/#booking",
};

export const formatFullAddress = (sep = ", ") =>
  [
    clinicNAP.address.street,
    clinicNAP.address.locality,
    `${clinicNAP.address.region} ${clinicNAP.address.postalCode}`,
    clinicNAP.address.country,
  ].join(sep);

/** Single-line address used for the FAQ schema and AI prompts. */
export const oneLineAddress = formatFullAddress(", ");

export const telHref = `tel:${clinicNAP.phone.international.replace(/\s+/g, "")}`;

/** WhatsApp click-to-chat link. */
export const whatsappHref = clinicNAP.whatsapp.url;

/** Google Maps "Open in Maps" link (search-style, no API key required). */
export const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  `${clinicNAP.name} ${clinicNAP.address.street} ${clinicNAP.address.postalCode}`,
)}`;

/** Google Maps embed src for an <iframe> (no API key required). */
export const mapsEmbedHref = `https://www.google.com/maps?q=${encodeURIComponent(
  `${clinicNAP.address.street}, ${clinicNAP.address.locality}, ${clinicNAP.address.region} ${clinicNAP.address.postalCode}, ${clinicNAP.address.country}`,
)}&output=embed`;

/** All public social URLs for use in schema `sameAs`. */
export const socialUrls: string[] = [clinicNAP.social.website, clinicNAP.social.instagram];
